import { format, isValid, parse } from 'date-fns';

export function formatRawDateDigits(rawInput: string, useUSFormat: boolean): string {
  const raw = rawInput.replace(/\D/g, '').slice(0, 8);
  let formatted = '';

  if (useUSFormat) {
    if (raw.length >= 2) formatted = raw.slice(0, 2);
    if (raw.length >= 4) formatted += '/' + raw.slice(2, 4);
    if (raw.length >= 5) formatted += '/' + raw.slice(4);
  } else {
    if (raw.length >= 4) formatted = raw.slice(0, 4);
    if (raw.length >= 6) formatted += '/' + raw.slice(4, 6);
    if (raw.length >= 7) formatted += '/' + raw.slice(6);
  }

  return formatted;
}

export function formatDateDisplay(date: Date, useUSFormat: boolean): string {
  if (!date || !isValid(date)) return '';
  return format(date, useUSFormat ? 'MM/dd/yyyy' : 'yyyy/MM/dd');
}

export function parseDateInput(dateInput: string | Date | null | undefined): Date | null {
  if (dateInput == null || dateInput === '') {
    return null;
  }
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format a date/time with the active BCP-47 culture via Intl.
 * Prefer this over Angular's `date` pipe (fixed bootstrap LOCALE_ID) or
 * `toLocale*(undefined)` (device OS locale, not in-app language).
 *
 * Supported format keys (Angular-like aliases kept for template drop-in):
 * - `EEEE HH:mm` / `weekdayTime`
 * - `mediumDate`
 * - `medium`
 * - `shortTime`
 * - `MMM dd yyyy` / `monthDayYear`
 * - `monthDayYearTime`
 * - `shortMonthDay`
 * - `weekdayLongDate` (weekday + medium date)
 * - `weekdayDateTime` (weekday + long date + 24h time) — emotion history
 */
export function formatLocalizedDate(
  dateInput: string | Date | null | undefined,
  culture: string,
  formatKey: string = 'mediumDate',
): string {
  const date = parseDateInput(dateInput);
  if (!date) {
    return '';
  }
  const locale = culture || 'en-ZA';

  switch (formatKey) {
    case 'EEEE HH:mm':
    case 'weekdayTime': {
      const weekday = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
      const time = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
      return `${weekday} ${time}`;
    }
    case 'mediumDate':
      return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date);
    case 'medium':
      return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
    case 'shortTime':
      return new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
    case 'MMM dd yyyy':
    case 'monthDayYear':
      return new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date);
    case 'monthDayYearTime': {
      const datePart = new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date);
      const timePart = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
      return `${datePart} ${timePart}`;
    }
    case 'shortMonthDay':
      return new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
      }).format(date);
    case 'weekdayLongDate': {
      const weekday = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
      const rest = new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date);
      return `${weekday}, ${rest}`;
    }
    case 'weekdayDateTime':
    case 'emotionHistory':
      return formatLocalizedWeekdayDateTime(date, locale);
    default:
      return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date);
  }
}

/**
 * Weekday + long month/day + 24h time using the active BCP-47 culture
 * (e.g. emotion history). Prefer this over Angular `date` pipe, which stays
 * on the bootstrap LOCALE_ID and does not follow in-app language changes.
 */
export function formatLocalizedWeekdayDateTime(
  dateInput: string | Date | null | undefined,
  culture: string,
): string {
  const date = parseDateInput(dateInput);
  if (!date) {
    return '';
  }
  const locale = culture || 'en-ZA';
  const datePart = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date);
  const timePart = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
  return `${datePart}, ${timePart}`;
}

export function formatIsoDateOnly(date: Date): string {
  if (!date || !isValid(date)) return '';
  return format(date, 'yyyy-MM-dd');
}

/**
 * Parse a date from ISO (yyyy-MM-dd / date-time), US (MM/dd/yyyy), or year-first (yyyy/MM/dd).
 * Slash formats are tried preferred-first then the other; they are not ambiguous for complete dates.
 */
export function parseFlexibleDate(dateStr: string, useUSFormat?: boolean): Date | null {
  if (!dateStr || typeof dateStr !== 'string') {
    return null;
  }
  const trimmed = dateStr.trim();
  if (!trimmed) {
    return null;
  }

  const datePart = trimmed.includes('T') ? trimmed.split('T')[0] : trimmed;

  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const parsed = parse(datePart, 'yyyy-MM-dd', new Date());
    return isValid(parsed) ? parsed : null;
  }

  if (datePart.includes('/')) {
    const preferred = useUSFormat ? 'MM/dd/yyyy' : 'yyyy/MM/dd';
    const fallback = useUSFormat ? 'yyyy/MM/dd' : 'MM/dd/yyyy';
    for (const fmt of [preferred, fallback]) {
      const parsed = parse(datePart, fmt, new Date());
      if (isValid(parsed)) {
        return parsed;
      }
    }
  }

  return null;
}

/**
 * Convert a displayed date, ISO date, ISO date-time, or Date to yyyy-MM-dd for the server.
 * Zoned date-times (JSON Date) use local calendar day; unzoned `yyyy-MM-ddTHH:mm:ss` uses the date part.
 */
export function toIsoDateOfBirth(value: string | Date | null | undefined, fromUSFormat?: boolean): string | null {
  if (value instanceof Date) {
    return isValid(value) ? formatIsoDateOnly(value) : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.includes('T')) {
    const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(trimmed);
    if (hasZone) {
      const instant = new Date(trimmed);
      if (!isNaN(instant.getTime())) {
        return formatIsoDateOnly(instant);
      }
    }
    const datePart = trimmed.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
  }

  const parsed = parseFlexibleDate(trimmed, fromUSFormat);
  return parsed ? formatIsoDateOnly(parsed) : null;
}
